import { useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import Supercluster from "supercluster";
import { apiV2 } from "../../../api/axiosConfig";
import { useMapSettings } from "../../operational-map/context/useMapSettings";

// --- Types ---

export type MapDevice = {
    _id: string;
    clientID: string;
    clientName: string;
    departmentID: string;
    name: string;
    deviceID: string;
    type: string;
    subtype: string;
    coordinates: number[];
    receivedAt: string;
    source: string;
};

export type DevicePopupState = {
    visible: boolean;
    position: { x: number; y: number };
    devices: MapDevice[];
};

// --- WebGL helpers ---

function compileShader(gl: WebGLRenderingContext, type: number, src: string): WebGLShader {
    const shader = gl.createShader(type)!;
    gl.shaderSource(shader, src);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        throw new Error(gl.getShaderInfoLog(shader) ?? "Shader compile error");
    }
    return shader;
}

function buildProgram(gl: WebGLRenderingContext, vert: string, frag: string): WebGLProgram {
    const vs = compileShader(gl, gl.VERTEX_SHADER, vert);
    const fs = compileShader(gl, gl.FRAGMENT_SHADER, frag);
    const prog = gl.createProgram()!;
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    return prog;
}

function loadTexture(gl: WebGLRenderingContext, src: string, onLoad?: () => void): WebGLTexture {
    const tex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 0]));
    const img = new Image();
    img.onload = () => {
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, 1);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, 0);
        gl.generateMipmap(gl.TEXTURE_2D);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        const ext = gl.getExtension("EXT_texture_filter_anisotropic")
                 ?? gl.getExtension("MOZ_EXT_texture_filter_anisotropic")
                 ?? gl.getExtension("WEBKIT_EXT_texture_filter_anisotropic");
        if (ext) gl.texParameterf(gl.TEXTURE_2D, ext.TEXTURE_MAX_ANISOTROPY_EXT, 4);
        onLoad?.();
    };
    img.src = src;
    return tex;
}

// --- Shaders ---

const ICON_VERT = `
    attribute vec2 a_position;
    attribute float a_highlighted;
    attribute vec3 a_color;
    varying vec3 v_color;
    void main() {
        gl_Position = vec4(a_position, 0.0, 1.0);
        gl_PointSize = a_highlighted > 0.5 ? 32.0 : 24.0;
        v_color = a_color;
    }
`;

const ICON_FRAG = `
    #extension GL_EXT_shader_texture_lod : enable
    precision mediump float;
    uniform sampler2D u_texture;
    varying vec3 v_color;
    void main() {
        vec2  uv   = gl_PointCoord - 0.5;
        float dist = length(uv);

        float sm        = 0.04;
        float fillR     = 0.5;
        float iconScale = 0.71;

        if (dist > fillR) discard;

        float fillAlpha = smoothstep(fillR, fillR - sm, dist);
        vec4 col = vec4(v_color, fillAlpha);

        vec2 iconUV = (gl_PointCoord - 0.5) / iconScale + 0.5;
        if (iconUV.x >= 0.0 && iconUV.x <= 1.0 && iconUV.y >= 0.0 && iconUV.y <= 1.0) {
            #ifdef GL_EXT_shader_texture_lod
                vec4 iconColor = texture2DLodEXT(u_texture, iconUV, 0.0);
            #else
                vec4 iconColor = texture2D(u_texture, iconUV, -1.0);
            #endif
            col.rgb = iconColor.rgb + col.rgb * (1.0 - iconColor.a);
        }

        if (col.a < 0.01) discard;
        gl_FragColor = vec4(col.rgb * col.a, col.a);
    }
`;

// --- Icon / color config ---

const DEVICE_ICON  = "/assets/mapicons/satellite_transparent.png";
const DEVICE_COLOR: [number, number, number] = [0.945, 0.247, 0.447]; // #f13f72 pink

const CLUSTER_COLOR = "#f13f72";
const CLUSTER_TEXT  = "#ffffff";

const CLICK_RADIUS   = 30;
const CLUSTER_RADIUS = 50;
const CLUSTER_MAX_ZOOM = 14;

// --- Rendered point for hit testing ---

type RenderedPoint = {
    x: number;
    y: number;
    device?: MapDevice;
    isCluster: boolean;
    clusterID?: number;
    count?: number;
};

// --- Overlay factory ---

function createDevicesOverlay(
    map: google.maps.Map,
    onRenderedPoints: (pts: RenderedPoint[]) => void,
    onRedraw: () => void,
) {
    const glCanvas = document.createElement("canvas");
    glCanvas.style.position = "absolute";
    glCanvas.style.top = "0";
    glCanvas.style.left = "0";
    glCanvas.style.pointerEvents = "none";
    map.getDiv().appendChild(glCanvas);

    const clusterCanvas = document.createElement("canvas");
    clusterCanvas.style.position = "absolute";
    clusterCanvas.style.top = "0";
    clusterCanvas.style.left = "0";
    clusterCanvas.style.pointerEvents = "none";
    map.getDiv().appendChild(clusterCanvas);

    const gl = glCanvas.getContext("webgl", { alpha: true, premultipliedAlpha: true })!;
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    const prog = buildProgram(gl, ICON_VERT, ICON_FRAG);
    const tex = loadTexture(gl, DEVICE_ICON, onRedraw);

    let isReady = false;
    const overlay = new (class extends google.maps.OverlayView {
        onAdd()    { isReady = true; }
        draw()     {}
        onRemove() { isReady = false; }
        getProjectionSafe(): google.maps.MapCanvasProjection | null {
            if (!isReady) return null;
            try { return this.getProjection(); } catch { return null; }
        }
    })();
    overlay.setMap(map);

    type DrawPoint = { lng: number; lat: number; device?: MapDevice; isCluster: boolean; clusterID?: number; count?: number };
    let drawPoints: DrawPoint[] = [];
    let hoveredIDs = new Set<string>();
    let rafId: number | null = null;

    function resizeCanvases(width: number, height: number, dpr: number) {
        for (const c of [glCanvas, clusterCanvas]) {
            c.style.width  = `${width}px`;
            c.style.height = `${height}px`;
            c.width  = width  * dpr;
            c.height = height * dpr;
        }
    }

    function draw() {
        const projection = overlay.getProjectionSafe();
        if (!projection) return;

        const mapDiv = map.getDiv() as HTMLElement;
        const dpr    = window.devicePixelRatio || 1;
        const width  = mapDiv.offsetWidth;
        const height = mapDiv.offsetHeight;
        if (!width || !height) return;

        resizeCanvases(width, height, dpr);

        gl.viewport(0, 0, glCanvas.width, glCanvas.height);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);

        const ctx2d = clusterCanvas.getContext("2d")!;
        ctx2d.clearRect(0, 0, clusterCanvas.width, clusterCanvas.height);
        ctx2d.scale(dpr, dpr);

        const allPts: RenderedPoint[] = [];

        if (drawPoints.length === 0) {
            ctx2d.setTransform(1, 0, 0, 1, 0, 0);
            onRenderedPoints(allPts);
            return;
        }

        const individuals = drawPoints.filter((dp) => !dp.isCluster);
        const clusters    = drawPoints.filter((dp) => dp.isCluster);

        // Draw individual icons via WebGL
        const positions: number[] = [];
        const highlighted: number[] = [];
        const colors: number[] = [];
        const pts: RenderedPoint[] = [];

        for (const dp of individuals) {
            if (!isFinite(dp.lng) || !isFinite(dp.lat)) continue;
            const pixel = projection.fromLatLngToContainerPixel(new google.maps.LatLng(dp.lat, dp.lng));
            if (!pixel) continue;

            const nx = (pixel.x / width) * 2 - 1;
            const ny = 1 - (pixel.y / height) * 2;

            positions.push(nx, ny);
            highlighted.push(dp.device && hoveredIDs.has(dp.device._id) ? 1.0 : 0.0);
            colors.push(...DEVICE_COLOR);
            pts.push({ x: pixel.x, y: pixel.y, device: dp.device, isCluster: false });
        }

        if (positions.length > 0) {
            gl.useProgram(prog);
            const aPosLoc   = gl.getAttribLocation(prog, "a_position");
            const aHlLoc    = gl.getAttribLocation(prog, "a_highlighted");
            const aColorLoc = gl.getAttribLocation(prog, "a_color");
            const count = positions.length / 2;

            const posBuf = gl.createBuffer()!;
            gl.bindBuffer(gl.ARRAY_BUFFER, posBuf);
            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.DYNAMIC_DRAW);
            gl.enableVertexAttribArray(aPosLoc);
            gl.vertexAttribPointer(aPosLoc, 2, gl.FLOAT, false, 0, 0);

            const hlBuf = gl.createBuffer()!;
            gl.bindBuffer(gl.ARRAY_BUFFER, hlBuf);
            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(highlighted), gl.DYNAMIC_DRAW);
            gl.enableVertexAttribArray(aHlLoc);
            gl.vertexAttribPointer(aHlLoc, 1, gl.FLOAT, false, 0, 0);

            const colorBuf = gl.createBuffer()!;
            gl.bindBuffer(gl.ARRAY_BUFFER, colorBuf);
            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(colors), gl.DYNAMIC_DRAW);
            gl.enableVertexAttribArray(aColorLoc);
            gl.vertexAttribPointer(aColorLoc, 3, gl.FLOAT, false, 0, 0);

            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, tex);
            gl.uniform1i(gl.getUniformLocation(prog, "u_texture"), 0);

            gl.drawArrays(gl.POINTS, 0, count);
            allPts.push(...pts);
        }

        // Draw cluster bubbles via 2D canvas
        for (const dp of clusters) {
            if (!isFinite(dp.lng) || !isFinite(dp.lat)) continue;
            const pixel = projection.fromLatLngToContainerPixel(new google.maps.LatLng(dp.lat, dp.lng));
            if (!pixel) continue;

            const hovered = dp.clusterID !== undefined && hoveredIDs.has(`cluster_${dp.clusterID}`);
            const r = hovered ? 17 : 12;
            ctx2d.beginPath();
            ctx2d.arc(pixel.x, pixel.y, r, 0, Math.PI * 2);
            ctx2d.fillStyle = CLUSTER_COLOR;
            ctx2d.fill();

            ctx2d.fillStyle = CLUSTER_TEXT;
            ctx2d.font = `bold ${hovered ? 13 : 10}px sans-serif`;
            ctx2d.textAlign = "center";
            ctx2d.textBaseline = "middle";
            ctx2d.fillText(String(dp.count ?? 0), pixel.x, pixel.y + 1);

            allPts.push({ x: pixel.x, y: pixel.y, isCluster: true, clusterID: dp.clusterID, count: dp.count });
        }

        ctx2d.setTransform(1, 0, 0, 1, 0, 0);
        onRenderedPoints(allPts);
    }

    function startLoop() {
        if (rafId !== null) return;
        const loop = () => {
            draw();
            rafId = drawPoints.length > 0 ? requestAnimationFrame(loop) : null;
        };
        rafId = requestAnimationFrame(loop);
    }

    const mapListeners = [
        google.maps.event.addListener(map, "idle",           draw),
        google.maps.event.addListener(map, "bounds_changed", draw),
    ];

    return {
        setDrawPoints(next: DrawPoint[]) {
            drawPoints = next;
            if (next.length > 0) startLoop();
            else draw();
        },
        setHoveredIDs(ids: Set<string>) {
            hoveredIDs = ids;
            draw();
        },
        destroy() {
            if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; }
            mapListeners.forEach((l) => google.maps.event.removeListener(l));
            overlay.setMap(null);
            if (glCanvas.parentNode) glCanvas.parentNode.removeChild(glCanvas);
            if (clusterCanvas.parentNode) clusterCanvas.parentNode.removeChild(clusterCanvas);
        },
    };
}

type DevicesOverlay = ReturnType<typeof createDevicesOverlay>;

// --- Supercluster ---

type DeviceClusterProps = { device: MapDevice };

function buildClusterIndex(devices: MapDevice[]): Supercluster<DeviceClusterProps> {
    const index = new Supercluster<DeviceClusterProps>({ radius: CLUSTER_RADIUS, maxZoom: CLUSTER_MAX_ZOOM });
    const points: GeoJSON.Feature<GeoJSON.Point, DeviceClusterProps>[] = devices.map((d) => ({
        type: "Feature",
        properties: { device: d },
        geometry: { type: "Point", coordinates: [d.coordinates[0], d.coordinates[1]] },
    }));
    index.load(points);
    return index;
}

// --- Fetch ---

async function fetchDevices(sources: string[], clientID: string | undefined): Promise<MapDevice[]> {
    const res = await apiV2.get("ops/devices", {
        params: { sources: sources.join(","), clientID: clientID ?? undefined },
    });
    return res.data as MapDevice[];
}

// --- Push clusters to overlay ---

function pushClusters(
    map: google.maps.Map | null,
    overlay: DevicesOverlay | null,
    clusterIndex: Supercluster<DeviceClusterProps> | null,
    devices: MapDevice[],
    cluster: boolean,
) {
    if (!overlay) return;
    if (!map || devices.length === 0) {
        overlay.setDrawPoints([]);
        return;
    }

    if (!cluster) {
        overlay.setDrawPoints(devices.map((d) => ({ lng: d.coordinates[0], lat: d.coordinates[1], isCluster: false, device: d })));
        return;
    }

    const zoom   = Math.floor(map.getZoom() ?? 0);
    const bounds = map.getBounds();
    if (!bounds) return;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const bbox: [number, number, number, number] = [sw.lng(), sw.lat(), ne.lng(), ne.lat()];

    if (!clusterIndex) {
        overlay.setDrawPoints([]);
        return;
    }

    const drawPoints = clusterIndex.getClusters(bbox, zoom).map((c) => {
        const [lng, lat] = c.geometry.coordinates;
        const props = c.properties as any;
        if (props.cluster) {
            return { lng, lat, isCluster: true, clusterID: props.cluster_id as number, count: props.point_count as number };
        }
        return { lng, lat, isCluster: false, device: (props as DeviceClusterProps).device };
    });

    overlay.setDrawPoints(drawPoints);
}

// --- Hook ---

export type DevicesLayerResult = {
    popup: DevicePopupState;
    setPopup: (s: DevicePopupState) => void;
    getHitsAtPoint: (clientX: number, clientY: number) => { devices: MapDevice[]; isCluster: boolean; hoveredID: string | null };
    setHoveredIDs: (ids: Set<string>) => void;
    openPopupForDevices: (devices: MapDevice[], position: { x: number; y: number }) => void;
    deviceCount: number;
};

export function useDevicesLayer(map: google.maps.Map | null): DevicesLayerResult {
    const { settings } = useMapSettings();
    const { showDevices, clientID } = settings;

    const overlayRef       = useRef<DevicesOverlay | null>(null);
    const renderedPointsRef = useRef<RenderedPoint[]>([]);
    const clusterIndexRef  = useRef<Supercluster<DeviceClusterProps> | null>(null);
    const devicesRef       = useRef<MapDevice[]>([]);

    const [popup, setPopup] = useState<DevicePopupState>({ visible: false, position: { x: 0, y: 0 }, devices: [] });
    const [deviceCount, setDeviceCount] = useState(0);

    const sources = showDevices ? ["satellite"] : [];

    const { data } = useQuery(
        ["device-locations-layer", showDevices, clientID],
        () => fetchDevices(sources, clientID),
        { refetchInterval: 60_000, enabled: showDevices }
    );

    // Overlay lifecycle
    useEffect(() => {
        if (!map) return;

        const overlay = createDevicesOverlay(
            map,
            (pts) => { renderedPointsRef.current = pts; },
            () => { pushClusters(map, overlayRef.current, clusterIndexRef.current, devicesRef.current, true); }
        );
        overlayRef.current = overlay;

        const listeners = [
            google.maps.event.addListener(map, "idle",           () => pushClusters(map, overlayRef.current, clusterIndexRef.current, devicesRef.current, true)),
            google.maps.event.addListener(map, "bounds_changed", () => pushClusters(map, overlayRef.current, clusterIndexRef.current, devicesRef.current, true)),
        ];

        return () => {
            overlay.destroy();
            overlayRef.current = null;
            renderedPointsRef.current = [];
            listeners.forEach((l) => google.maps.event.removeListener(l));
        };
    }, [map]);

    // Rebuild cluster index when data or showDevices changes
    useEffect(() => {
        const next = showDevices ? (data ?? []) : [];
        devicesRef.current = next;
        setDeviceCount(next.length);
        clusterIndexRef.current = next.length > 0 ? buildClusterIndex(next) : null;
        pushClusters(map, overlayRef.current, clusterIndexRef.current, next, true);
    }, [data, showDevices, map]);

    const getHitsAtPoint = (clientX: number, clientY: number): { devices: MapDevice[]; isCluster: boolean; hoveredID: string | null } => {
        if (!map) return { devices: [], isCluster: false, hoveredID: null };
        const mapDiv = map.getDiv() as HTMLElement;
        const rect = mapDiv.getBoundingClientRect();
        const x = clientX - rect.left;
        const y = clientY - rect.top;

        let closest: { pt: RenderedPoint; dist: number } | null = null;
        for (const pt of renderedPointsRef.current) {
            const dist = Math.hypot(pt.x - x, pt.y - y);
            if (dist <= CLICK_RADIUS && (!closest || dist < closest.dist)) {
                closest = { pt, dist };
            }
        }

        if (!closest) return { devices: [], isCluster: false, hoveredID: null };

        if (closest.pt.isCluster && closest.pt.clusterID !== undefined && clusterIndexRef.current) {
            const leaves = clusterIndexRef.current.getLeaves(closest.pt.clusterID, Infinity);
            return { devices: leaves.map((l) => l.properties.device), isCluster: true, hoveredID: `cluster_${closest.pt.clusterID}` };
        }

        if (closest.pt.device) return { devices: [closest.pt.device], isCluster: false, hoveredID: closest.pt.device._id };
        return { devices: [], isCluster: false, hoveredID: null };
    };

    const setHoveredIDs = (ids: Set<string>) => {
        overlayRef.current?.setHoveredIDs(ids);
    };

    const openPopupForDevices = (devices: MapDevice[], position: { x: number; y: number }) => {
        setPopup({ visible: true, position, devices });
    };

    return { popup, setPopup, getHitsAtPoint, setHoveredIDs, openPopupForDevices, deviceCount };
}
